import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { Player } from "@/lib/types";
import { getFirmDashboard, getPlayer } from "@/sideEffects/api";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useQuery } from "@tanstack/react-query";
import { ContentsPieChart } from "./ContentsPieChart";
import { UpdateRowButton } from "@/components/ax/dataTable/UpdateRowButton";
import { SelectedMediaPreview } from "../shared/SelectedMediaPreview";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { MetricCard } from "./MetricCard";
import {
  CopyrightIcon,
  ImageIcon,
  TabletIcon,
  TvIcon,
  UsersIcon,
  DatabaseIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  ActivityIcon,
  EyeIcon,
} from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { ROUTES } from "@/constants/routeConstants";
import { PermissionChecker2 } from "@/components/permission/PermissionChecker2";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { DashboardPlayerHealthStatus } from "./DashboardPlayerHealthStatus";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { RequestNewLicenceButton } from "./RequestNewLicenceButton";
import { motion, AnimatePresence } from "framer-motion";

// Animation variants for the page container
const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1.0],
    },
  },
  exit: {
    opacity: 0,
    y: 15,
    transition: {
      duration: 0.3,
    },
  },
};

// Animation variants for sections
const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 80,
      damping: 15,
      mass: 1,
      when: "beforeChildren",
      staggerChildren: 0.07,
    },
  },
};

// Animation variants for cards
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

// Animation variants for text elements
const textVariants = {
  hidden: {
    opacity: 0,
    x: -5,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 10,
      duration: 0.1,
      mass: 0.5,
    },
  },
};

// Animation variants for metric cards
const metricCardVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: (custom: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      delay: custom * 0.05,
    },
  }),
  hover: {
    scale: 1.03,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 10,
    },
  },
};

// Animation for the progress bar
const progressVariants = {
  hidden: { width: 0 },
  visible: (value: number) => ({
    width: `${value}%`,
    transition: {
      duration: 1,
      ease: "easeOut",
      delay: 0.3,
    },
  }),
};

export const HomePage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  useBreadcrumbs([
    {
      name: t("Dashboard"),
      path: ROUTES.HOME,
    },
  ]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["getFirmDashboard", String(activeFirm?.firmGuid)],
    queryFn: () => getFirmDashboard(String(activeFirm?.firmGuid)),
    enabled: !!activeFirm?.firmGuid,
  });

  const { goToPlayerDetail } = useAppNavigate();

  const columns: ColumnDef<Player>[] = [
    {
      accessorKey: "name",
      header: t("common.name"),
    },
    {
      accessorKey: "timezone",
      header: t("entities.timeZone"),
    },
    {
      // use this for uniq header purpose or table complains
      accessorKey: "code",
      header: t("playerInfo.healthStatus"),
      cell: ({ row }) => {
        return <DashboardPlayerHealthStatus playerId={row.original.intId} />;
      },
    },
    {
      accessorKey: "defaultContentintId",
      header: () => t("content.defaultContent"),
      cell: ({ row }) => {
        return <SelectedMediaPreview contentIntId={row.original.defaultContentintId} />;
      },
    },
    {
      accessorKey: "id",
      header: () => t("common.media"),
      cell: ({ row }) => {
        return (
          <>
            <SelectedMediaPreview
              channelIntId={row.original.channelintId}
              contentGroupIntId={row.original.contentGroupintId}
              contentIntId={row.original.contentintId}
            />
            <PermissionChecker2 entity="Player" rights={["GetDetails"]}>
              <div className="flex items-center justify-end gap-2">
                <UpdateRowButton onClickUpdate={() => goToPlayerDetail(row.original.id)} />
              </div>
            </PermissionChecker2>
          </>
        );
      },
    },
  ];

  if (!activeFirm?.firmGuid) return <div>Welcome to Corevo Digital</div>;

  const dasboard = data?.data;
  if (isLoading) return <LoadingSmall />;
  if (error || !dasboard)
    return (
      <Alert className="mt-2" variant="destructive">
        <AlertTitle>{t("common.error")}</AlertTitle>
        <AlertDescription>{error?.message || t("dataTable.error")}</AlertDescription>
      </Alert>
    );

  // Calculate health percentage for animation
  const healthPercentage =
    dasboard.totalHealthyDevice + dasboard.totalUnhealthyDevice > 0
      ? (dasboard.totalHealthyDevice / (dasboard.totalHealthyDevice + dasboard.totalUnhealthyDevice)) * 100
      : 0;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="space-y-6 px-1 py-2 md:px-4 md:py-6"
        variants={pageVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Header Section with Firm Info */}
        <motion.div className="flex flex-col space-y-2" variants={sectionVariants}>
          <div className="flex items-center justify-between">
            <motion.h1 className="text-2xl font-semibold tracking-tight" variants={textVariants}>
              {t("Dashboard")}
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <Badge
                variant="outline"
                className="px-3 py-1 text-sm font-medium backdrop-blur-sm bg-white/30 dark:bg-black/30 border-white/20 dark:border-white/10"
              >
                {activeFirm?.firmName}
              </Badge>
            </motion.div>
          </div>
          <motion.p className="text-muted-foreground" variants={textVariants}>
            {t("dashboard.welcomeMessage", { firmName: activeFirm?.firmName })}
          </motion.p>
        </motion.div>

        {/* Key Metrics Section */}
        <motion.div variants={sectionVariants}>
          <motion.h2 className="text-lg font-medium mb-3" variants={textVariants}>
            {t("dashboard.keyMetrics")}
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <motion.div custom={0} variants={metricCardVariants} whileHover="hover">
              <MetricCard
                title={t("dashboard.totalPlayers")}
                value={dasboard.totalPlayers}
                icon={<TabletIcon className="w-5 h-5 text-indigo-500" />}
                className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-950/30 dark:to-indigo-900/20 border-none shadow-sm"
                description={t("dashboard.activePlayers") + ": " + dasboard.totalActivePlayers}
              />
            </motion.div>
            <motion.div custom={1} variants={metricCardVariants} whileHover="hover">
              <MetricCard
                title={t("dashboard.totalContent")}
                value={dasboard.totalContent}
                icon={<ImageIcon className="w-5 h-5 text-blue-500" />}
                className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border-none shadow-sm"
                description={t("dashboard.totalContentSize") + ": " + dasboard.totalContentSize.toFixed(2) + " MB"}
              />
            </motion.div>
            <motion.div custom={2} variants={metricCardVariants} whileHover="hover">
              <MetricCard
                title={t("dashboard.totalChannel")}
                value={dasboard.totalChannel}
                icon={<TvIcon className="w-5 h-5 text-emerald-500" />}
                className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20 border-none shadow-sm"
              />
            </motion.div>
            {/* <motion.div custom={3} variants={metricCardVariants} whileHover="hover">
              <MetricCard
                title={t("dashboard.totalUsers")}
                value={dasboard.totalUsers}
                icon={<UsersIcon className="w-5 h-5 text-violet-500" />}
                className="bg-gradient-to-br from-violet-50 to-violet-100 dark:from-violet-950/30 dark:to-violet-900/20 border-none shadow-sm"
              />
            </motion.div> */}
            <motion.div custom={3} variants={metricCardVariants} whileHover="hover">
              <MetricCard
                title={t("dashboard.totalViewLastDay")}
                value={dasboard.totalViewLastDay || 0}
                icon={<EyeIcon className="w-5 h-5 text-amber-500" />}
                className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-900/20 border-none shadow-sm"
                description={t("dashboard.totalViewLastDayDesc")}
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Content and Health Status Section */}
        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" variants={sectionVariants}>
          {/* Content Distribution */}
          <motion.div className="md:col-span-2" variants={cardVariants}>
            <ContentsPieChart dasboard={dasboard} />
          </motion.div>

          {/* Device Health Status */}
          <motion.div className="space-y-4">
            <motion.div variants={cardVariants} whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}>
              <Card className="overflow-hidden border-none shadow-sm bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-950/50 dark:to-slate-900/30">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium">{t("dashboard.deviceHealthStatus")}</CardTitle>
                    <motion.div
                      initial={{ rotate: -10, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      <ActivityIcon className="h-5 w-5 text-slate-500" />
                    </motion.div>
                  </div>
                  <CardDescription>
                    {t("dashboard.totalDevices")}: {dasboard.totalHealthyDevice + dasboard.totalUnhealthyDevice}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between mb-2 text-sm">
                    <div className="flex items-center gap-2">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.6, type: "spring", stiffness: 500 }}
                      >
                        <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                      </motion.div>
                      <span>
                        {t("dashboard.totalHealthyDevice")}: {dasboard.totalHealthyDevice}
                      </span>
                    </div>
                    <motion.div
                      className="font-medium"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      {Math.round(healthPercentage)}%
                    </motion.div>
                  </div>
                  <div className="h-2.5 rounded-full bg-red-100 overflow-hidden">
                    <motion.div
                      className="h-full bg-emerald-500 rounded-full"
                      custom={healthPercentage}
                      initial="hidden"
                      animate="visible"
                      variants={progressVariants}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                    <motion.span
                      className="flex items-center gap-1"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 }}
                    >
                      <AlertCircleIcon className="w-3 h-3 text-red-500" />
                      {t("dashboard.totalUnhealthyDevice")}: {dasboard.totalUnhealthyDevice}
                    </motion.span>
                    <motion.span
                      className="flex items-center gap-1"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.9 }}
                    >
                      <DatabaseIcon className="w-3 h-3 text-blue-500" />
                      {t("dashboard.totalContentSize")}: {dasboard.totalContentSize.toFixed(2)} MB
                    </motion.span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* License Status */}
            <motion.div variants={cardVariants} whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}>
              <Card className="border-none shadow-sm bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium flex items-center justify-between">
                    <div>{t("dashboard.licenseStatus")}</div>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <RequestNewLicenceButton />
                    </motion.div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <motion.div
                    className="flex items-center justify-between"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="flex items-center gap-2">
                      <motion.div
                        animate={{
                          rotate: [0, 10, 0],
                          scale: [1, 1.1, 1],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          repeatDelay: 5,
                        }}
                      >
                        <CopyrightIcon className="w-4 h-4 text-green-600" />
                      </motion.div>
                      <span className="text-sm">{t("dashboard.totalAssignedLicence")}</span>
                    </div>
                    <span className="font-medium">{dasboard.totalAssignedLicence}</span>
                  </motion.div>
                  <Separator />
                  <motion.div
                    className="flex items-center justify-between"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <div className="flex items-center gap-2">
                      <CopyrightIcon className="w-4 h-4 text-red-500" />
                      <span className="text-sm">{t("dashboard.totalExpiredLicence")}</span>
                    </div>
                    <span className="font-medium">{dasboard.totalExpiredLicence}</span>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Players Table Section */}
        <motion.div variants={sectionVariants}>
          <div className="flex items-center justify-between mb-4">
            <motion.h2 className="text-lg font-medium" variants={textVariants}>
              {t("dashboard.totalPlayers")}
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -5 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              <Badge variant="secondary" className="px-2 py-1 backdrop-blur-sm bg-white/30 dark:bg-black/30">
                {t("dashboard.totalCount")}: {dasboard.totalPlayers}
              </Badge>
            </motion.div>
          </div>
          <PermissionChecker2 entity="Player" rights={["List"]}>
            <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
              <Card className="border-none shadow-sm overflow-hidden bg-white/50 dark:bg-black/20 backdrop-blur-sm">
                <CardContent className="p-0">
                  <AxDataTable
                    columns={columns}
                    fetcher={getPlayer}
                    getRowId={(row) => row.id.toString()}
                    uniqQueryKey="getPlayer"
                    dynamicRequestParams={{
                      Firm_Id: String(activeFirm?.firmGuid),
                    }}
                  />
                </CardContent>
              </Card>
            </motion.div>
          </PermissionChecker2>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
